import * as React from 'react'
import { Check, ChevronsUpDown, X } from 'lucide-react'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/utils/cn'

interface MultiSelectOption {
  value: string
  label: string
}

interface MultiSelectProps {
  options: MultiSelectOption[]
  value: string[]
  onValueChange: (value: string[]) => void
  placeholder?: string
  isLoading?: boolean
  className?: string
  popoverClassName?: string
  containScroll?: boolean
  onSearchChange?: (search: string) => void
  onOpenChange?: (open: boolean) => void
  onReachEnd?: () => void
  isFetchingMore?: boolean
  maxVisibleBadges?: number
  badgeClassName?: string
}

const DEFAULT_MAX_VISIBLE_BADGES = 2

export function MultiSelect({
  options,
  value,
  onValueChange,
  placeholder = 'Select...',
  isLoading = false,
  className,
  popoverClassName,
  containScroll = false,
  onSearchChange,
  onOpenChange,
  onReachEnd,
  isFetchingMore = false,
  maxVisibleBadges = DEFAULT_MAX_VISIBLE_BADGES,
  badgeClassName,
}: MultiSelectProps) {
  const [open, setOpen] = React.useState(false)

  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      setOpen(next)
      onOpenChange?.(next)
    },
    [onOpenChange]
  )
  const [search, setSearch] = React.useState('')
  const scrollElRef = React.useRef<HTMLDivElement | null>(null)
  const popoverElRef = React.useRef<HTMLDivElement | null>(null)
  const sentinelRef = React.useRef<HTMLDivElement>(null)

  // Native wheel handler: manually scrolls the container so mouse-wheel works
  // even when a Sheet/Dialog's react-remove-scroll blocks it.
  const wheelHandler = React.useCallback((e: WheelEvent) => {
    const el = scrollElRef.current
    if (!el) return
    el.scrollTop += e.deltaY
    e.preventDefault()
  }, [])

  const popoverRef = React.useCallback((node: HTMLDivElement | null) => {
    popoverElRef.current = node
  }, [])

  React.useEffect(() => {
    if (!open || !containScroll) return

    const containSearchAreaWheel = (event: WheelEvent) => {
      const popover = popoverElRef.current
      const list = scrollElRef.current
      const target = event.target
      if (
        !popover ||
        !(target instanceof Node) ||
        !popover.contains(target) ||
        list?.contains(target)
      ) {
        return
      }

      event.preventDefault()
      event.stopPropagation()
    }

    document.addEventListener('wheel', containSearchAreaWheel, {
      capture: true,
      passive: false,
    })
    return () =>
      document.removeEventListener('wheel', containSearchAreaWheel, true)
  }, [containScroll, open])

  // Callback ref: attaches the native wheel handler the moment the scroll
  // container mounts inside the Popover portal.
  const scrollRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      const prev = scrollElRef.current
      if (prev) {
        prev.removeEventListener('wheel', wheelHandler)
      }
      scrollElRef.current = node
      if (node) {
        node.addEventListener('wheel', wheelHandler, { passive: false })
      }
    },
    [wheelHandler]
  )

  const filtered = React.useMemo(() => {
    if (!search) return options
    const lower = search.toLowerCase()
    return options.filter(o => o.label.toLowerCase().includes(lower))
  }, [options, search])

  // Infinite scroll observer – depends on `open` so it re-runs once the
  // popover mounts (the sentinel element only exists while the popover is open).
  React.useEffect(() => {
    if (!open || !onReachEnd) return
    const sentinel = sentinelRef.current
    if (!sentinel) return

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) {
          onReachEnd()
        }
      },
      { root: scrollElRef.current, threshold: 0.1 }
    )

    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [open, onReachEnd])

  const toggle = (optionValue: string) => {
    if (value.includes(optionValue)) {
      onValueChange(value.filter(v => v !== optionValue))
    } else {
      onValueChange([...value, optionValue])
    }
  }

  const selectedLabels = React.useMemo(() => {
    const map = new Map(options.map(o => [o.value, o.label]))
    return value.map(v => map.get(v) ?? v)
  }, [options, value])

  const showAll = maxVisibleBadges === Infinity
  const visibleLabels = showAll
    ? selectedLabels
    : selectedLabels.slice(0, maxVisibleBadges)
  const hiddenCount = showAll ? 0 : Math.max(0, value.length - maxVisibleBadges)

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          className={cn(
            'flex min-h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm ring-offset-background',
            'placeholder:text-muted-foreground focus:outline-none focus-visible:ring-1 focus-visible:ring-ring',
            'disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer',
            className
          )}
        >
          <div className="flex flex-wrap gap-1 flex-1 min-w-0">
            {value.length === 0 && (
              <span className="text-muted-foreground">{placeholder}</span>
            )}
            {visibleLabels.map((label, i) => (
              <Badge
                key={value[i]}
                variant="secondary"
                className={cn(
                  'text-xs px-1.5 py-0 max-w-[120px] truncate',
                  badgeClassName
                )}
              >
                {label}
                <span
                  role="button"
                  tabIndex={-1}
                  aria-label={`Remove ${label}`}
                  className="ml-1 inline-flex ring-offset-background rounded-full outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 cursor-pointer"
                  onMouseDown={e => e.preventDefault()}
                  onClick={e => {
                    e.stopPropagation()
                    toggle(value[i])
                  }}
                >
                  <X className="h-3 w-3" />
                </span>
              </Badge>
            ))}
            {hiddenCount > 0 && (
              <Badge variant="secondary" className="text-xs px-1.5 py-0">
                +{hiddenCount} more
              </Badge>
            )}
          </div>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50 ml-2" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        ref={popoverRef}
        className={cn(
          'flex max-h-[300px] w-[--radix-popover-trigger-width] flex-col p-0',
          popoverClassName
        )}
        align="start"
      >
        <div className="shrink-0 px-2 pb-2 pt-1">
          <input
            className="flex h-8 w-full border-b border-input bg-transparent px-2 py-1 text-sm outline-none placeholder:text-muted-foreground"
            placeholder="Search..."
            value={search}
            onChange={e => {
              setSearch(e.target.value)
              onSearchChange?.(e.target.value)
            }}
          />
        </div>
        <div
          ref={scrollRef}
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-1 pb-1"
        >
          {isLoading && (
            <div className="py-6 text-center text-sm text-muted-foreground">
              Loading...
            </div>
          )}
          {!isLoading && filtered.length === 0 && (
            <div className="py-6 text-center text-sm text-muted-foreground">
              No results found.
            </div>
          )}
          {!isLoading &&
            filtered.map(option => {
              const isSelected = value.includes(option.value)
              return (
                <button
                  key={option.value}
                  type="button"
                  className={cn(
                    'relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none',
                    'hover:bg-accent hover:text-accent-foreground',
                    isSelected && 'bg-accent/50'
                  )}
                  onClick={() => toggle(option.value)}
                >
                  <span className="absolute left-2 flex h-4 w-4 items-center justify-center">
                    {isSelected && <Check className="h-4 w-4" />}
                  </span>
                  <span className="truncate">{option.label}</span>
                </button>
              )
            })}
          {onReachEnd && <div ref={sentinelRef} className="h-1" />}
          {isFetchingMore && (
            <div className="py-2 text-center text-xs text-muted-foreground">
              Loading more...
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
